import React, { useState, useEffect, useCallback, useRef } from "react";
import usePersistedState from '../../hooks/usePersistedState';
import useDebounce from '../../hooks/useDebounce';
import Paginate from '../../components/Paginate';
import { itemsPerPage, handleApiError } from '../../components/Utils';
import { Link } from 'react-router-dom';
import { FaSortUp, FaSortDown } from 'react-icons/fa';
import useClientService from '../../hooks/useClientService';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    TextField,
    Button,
    Grid,
    CircularProgress,
    Typography
} from "@mui/material";

function ListClients() {
    const { getClients } = useClientService();
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);
    const [fetchReset, setFetchReset] = useState(false);

    const [currentPage, setCurrentPage] = useState(1);
    const [totalItems, setTotalItems] = useState(null);

    const [clients, setClients] = useState([]);

    const [name, setName] = usePersistedState('list_clients_name', null);
    const [code, setCode] = usePersistedState('list_clients_code', null);
    const [documentation, setDocumentation] = usePersistedState('list_clients_documentation', null);
    const [email, setEmail] = usePersistedState('list_clients_email', null);
    const [telephoneNumber, setTelephoneNumber] = usePersistedState('list_clients_telephoneNumber', null);

    const debouncedName = useDebounce(name);
    const debouncedCode = useDebounce(code);
    const debouncedDocumentation = useDebounce(documentation);
    const debouncedEmail = useDebounce(email);
    const debouncedTelephoneNumber = useDebounce(telephoneNumber);

    const [sortColumn, setSortColumn] = usePersistedState('list_clients_sortColumn', '');
    const [sortDirection, setSortDirection] = usePersistedState('list_clients_sortDirection', 'ASC');

    const isFirstFetch = useRef(true);

    const fetchClients = useCallback(async () => {
        try {
            if (isFirstFetch.current) setLoading(true);
            setError(null);
            const params = {};
            params.limit = itemsPerPage;
            params.offset = (currentPage - 1) * itemsPerPage;

            if (!fetchReset) {
                if (name) params.name = name;
                if (code) params.code = code;
                if (email) params.email = email;
                if (telephoneNumber) params.telephone_number = telephoneNumber;
                if (documentation) params.documentation = documentation;
                if (sortColumn) params.sort_by = sortColumn;
                if (sortDirection) params.sort_direction = sortDirection;
            } else {
                setFetchReset(false);
            }

            const result = await getClients(params);

            setClients(result.data.items);
            setTotalItems(result.data.total);
            isFirstFetch.current = false;
        } catch (error) {
            setError(handleApiError(error, 'Error al cargar los clientes.'));
        } finally {
            setLoading(false);
        }
    }, [currentPage, fetchReset, code, name, email, telephoneNumber, documentation, sortColumn, sortDirection]);

    useEffect(() => {
        fetchClients();
    }, [currentPage, sortColumn, sortDirection, fetchReset, debouncedName, debouncedCode, debouncedDocumentation, debouncedEmail, debouncedTelephoneNumber]);

    const handleSort = (column) => {
        if (sortColumn === column) {
            setSortDirection(sortDirection === 'ASC' ? 'DESC' : 'ASC');
        } else {
            setSortColumn(column);
            setSortDirection('ASC');
        }
        setCurrentPage(1);
    };

    const handleResetFilters = () => {
        setCurrentPage(1);
        setName(null);
        setDocumentation(null);
        setEmail(null);
        setCode(null);
        setTelephoneNumber(null);
        setFetchReset(true);
    };

    const cellStyle = {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        maxWidth: '75px'
    };

    return (
        <div>
            {loading && (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
                    <CircularProgress />
                </div>
            )}
            {!loading && (
                <>
                    <Grid container spacing={1} alignItems="center">
                        <Grid item xs>
                            <Grid container spacing={1} alignItems="center">
                                <Grid item xs={3} sm={2}>
                                    <TextField
                                        fullWidth
                                        label="Código"
                                        name="code"
                                        value={code || ""}
                                        onChange={(e) => setCode(e.target.value)}
                                    />
                                </Grid>
                                <Grid item xs={3} sm={2}>
                                    <TextField
                                        fullWidth
                                        label="Nombre"
                                        name="name"
                                        value={name || ""}
                                        onChange={(e) => setName(e.target.value)}
                                    />
                                </Grid>
                                <Grid item xs={3} sm={2}>
                                    <TextField
                                        fullWidth
                                        label="Email"
                                        name="email"
                                        value={email || ""}
                                        onChange={(e) => setEmail(e.target.value)}
                                    />
                                </Grid>
                                <Grid item xs={3} sm={2}>
                                    <TextField
                                        fullWidth
                                        label="Documentación"
                                        name="documentation"
                                        value={documentation || ""}
                                        onChange={(e) => setDocumentation(e.target.value)}
                                    />
                                </Grid>
                                <Grid item xs={3} sm={2}>
                                    <TextField
                                        fullWidth
                                        label="Teléfono"
                                        name="telephone_number"
                                        value={telephoneNumber || ""}
                                        onChange={(e) => setTelephoneNumber(e.target.value)}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid item>
                            <Grid container spacing={1} justifyContent="flex-end">
                                <Grid item>
                                    <Button variant="contained" color="secondary" onClick={handleResetFilters}>
                                        Resetear Filtros
                                    </Button>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                    <br />
                    <Link to="/clients/new" style={{ textDecoration: 'none' }}>
                        <Button variant="contained" color="primary">
                            Crear Cliente
                        </Button>
                    </Link>
                    <br />
                    <br />
                    <TableContainer component={Paper}>
                        <Table aria-label="simple table">
                            <TableHead>
                                <TableRow>
                                    <TableCell>
                                        <Typography variant="h6" gutterBottom>
                                            <span onClick={() => handleSort('code')} style={{ cursor: 'pointer' }}>
                                                Código {sortColumn === 'code' && (sortDirection === 'ASC' ? <FaSortUp /> : <FaSortDown />)}
                                            </span>
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="h6" gutterBottom>
                                            <span onClick={() => handleSort('name')} style={{ cursor: 'pointer' }}>
                                                Nombre {sortColumn === 'name' && (sortDirection === 'ASC' ? <FaSortUp /> : <FaSortDown />)}
                                            </span>
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="h6" gutterBottom>
                                            <span onClick={() => handleSort('email')} style={{ cursor: 'pointer' }}>
                                                Email {sortColumn === 'email' && (sortDirection === 'ASC' ? <FaSortUp /> : <FaSortDown />)}
                                            </span>
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="h6" gutterBottom>
                                            <span onClick={() => handleSort('documentation')} style={{ cursor: 'pointer' }}>
                                                Documentación {sortColumn === 'documentation' && (sortDirection === 'ASC' ? <FaSortUp /> : <FaSortDown />)}
                                            </span>
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="h6" gutterBottom>
                                            <span onClick={() => handleSort('telephone_number')} style={{ cursor: 'pointer' }}>
                                                Teléfono {sortColumn === 'telephone_number' && (sortDirection === 'ASC' ? <FaSortUp /> : <FaSortDown />)}
                                            </span>
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="h6" gutterBottom>
                                            <span onClick={() => handleSort('address')} style={{ cursor: 'pointer' }}>
                                                Dirección {sortColumn === 'address' && (sortDirection === 'ASC' ? <FaSortUp /> : <FaSortDown />)}
                                            </span>
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {clients.map(item => (
                                    <TableRow key={item.id}>
                                        <TableCell component="th" scope="row" sx={cellStyle}>
                                            <Link to={`/clients/update/${item.id}`}>
                                                {item.code || '-'}
                                            </Link>
                                        </TableCell>
                                        <TableCell component="th" scope="row" sx={cellStyle}>
                                            {(item.name || item.surname) ? `${item.name || ''} ${item.surname || ''}` : '-'}
                                        </TableCell>
                                        <TableCell component="th" scope="row" sx={cellStyle}>
                                            {item.email || '-'}
                                        </TableCell>
                                        <TableCell component="th" scope="row" sx={cellStyle}>
                                            {item.documentation || '-'}
                                        </TableCell>
                                        <TableCell sx={cellStyle}>
                                            {item.telephone_number || '-'}
                                        </TableCell>
                                        <TableCell sx={cellStyle}>
                                            {item.address || '-'}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </>
            )}
            <br />
            <br />
            <br />
            <Paginate
                totalItems={totalItems}
                onPageChange={setCurrentPage}
                currentPage={currentPage}
            />
            {error && (
                <Typography variant="body2" color="error" style={{ marginTop: 10 }}>
                    {error}
                </Typography>
            )}
        </div>
    );
}

export default ListClients;
